import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, XCircle, Mail, ExternalLink } from "lucide-react";
import api from "../lib/api";
import { useCart } from "../contexts/CartContext";

const POLL_INTERVAL = 2000;
const MAX_ATTEMPTS = 15; // 15 × 2s = 30s before showing timeout UI

export default function Success() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { clear } = useCart();
  const [state, setState] = useState({ status: "checking", payment_status: null, order_number: null });
  const attemptsRef = useRef(0);
  const cleared = useRef(false);

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (!sessionId) {
      setState({ status: "error", payment_status: null, order_number: null });
      return;
    }

    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        setState(data);

        if (data.payment_status === "paid") {
          if (!cleared.current) {
            clear();
            cleared.current = true;
          }
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current < MAX_ATTEMPTS) {
          setTimeout(poll, POLL_INTERVAL);
        } else {
          setState((s) => ({ ...s, status: "timeout" }));
        }
      } catch (e) {
        setState({ status: "error", payment_status: null, order_number: null });
      }
    };

    poll();
    return () => {
      cancelled = true;
    };
  }, [sessionId]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const isPaid = state.payment_status === "paid";
  const isFail = state.status === "expired" || state.payment_status === "failed" || state.status === "error";
  const isPending = state.status === "timeout";

  return (
    <div className="max-w-2xl mx-auto px-5 py-32 text-center" data-testid="success-page">
      {isPaid ? (
        <>
          <CheckCircle2 className="w-20 h-20 text-[var(--terracotta)] mx-auto mb-6" />
          <p className="eyebrow mb-3">Order received</p>
          <h1 className="font-serif text-5xl text-[var(--ink-forest)] tracking-tight">
            Thank you!
          </h1>
          <p className="mt-4 text-[var(--muted-fg)] text-lg">
            Your order is on its way to being packed with care.
          </p>
          {state.order_number && (
            <p className="mt-8 text-sm text-[var(--ink-forest)]">
              Order number:{" "}
              <span className="font-mono font-semibold text-[var(--terracotta)]" data-testid="success-order-number">
                {state.order_number}
              </span>
            </p>
          )}
          <p className="mt-2 text-xs text-[var(--muted-fg)]">
            A confirmation will arrive in your inbox shortly.
          </p>
          <div className="mt-10 flex gap-3 justify-center flex-wrap">
            <Link
              to="/shop"
              className="bg-[var(--terracotta)] text-white rounded-full px-7 py-3 text-sm font-semibold hover:bg-[var(--terracotta-deep)] transition-colors"
              data-testid="success-continue-btn"
            >
              Continue shopping
            </Link>
            <Link
              to="/track"
              className="border border-[var(--border)] text-[var(--ink-forest)] rounded-full px-7 py-3 text-sm hover:bg-[var(--muted)] transition-colors"
            >
              Track order
            </Link>
          </div>
        </>
      ) : isFail ? (
        <>
          <XCircle className="w-20 h-20 text-[var(--muted-fg)] mx-auto mb-6" />
          <h1 className="font-serif text-4xl text-[var(--ink-forest)]">Payment didn&apos;t go through</h1>
          <p className="mt-3 text-[var(--muted-fg)]">No charges were made. Please try again.</p>
          <Link
            to="/cart"
            className="mt-8 inline-block bg-[var(--terracotta)] text-white rounded-full px-7 py-3 text-sm font-semibold"
          >
            Return to basket
          </Link>
        </>
      ) : isPending ? (
        <>
          <Mail className="w-20 h-20 text-[var(--terracotta)] mx-auto mb-6" />
          <p className="eyebrow mb-3">We&apos;re on it</p>
          <h1 className="font-serif text-4xl lg:text-5xl text-[var(--ink-forest)] tracking-tight">
            Your order is being confirmed
          </h1>
          <p className="mt-4 text-[var(--muted-fg)] text-lg leading-relaxed">
            Stripe is taking a little longer than usual to confirm your payment. No need to wait here —
            your order is safe and you&apos;ll receive a confirmation email as soon as it clears.
          </p>
          {state.order_number && (
            <p className="mt-6 text-sm text-[var(--ink-forest)]">
              Order number:{" "}
              <span className="font-mono font-semibold text-[var(--terracotta)]" data-testid="pending-order-number">
                {state.order_number}
              </span>
            </p>
          )}
          <div className="mt-10 flex gap-3 justify-center flex-wrap">
            <Link
              to="/track"
              className="bg-[var(--terracotta)] text-white rounded-full px-7 py-3 text-sm font-semibold hover:bg-[var(--terracotta-deep)] transition-colors inline-flex items-center gap-2"
              data-testid="pending-track-btn"
            >
              <ExternalLink className="w-4 h-4" /> Track your order
            </Link>
            <Link
              to="/shop"
              className="border border-[var(--border)] text-[var(--ink-forest)] rounded-full px-7 py-3 text-sm hover:bg-[var(--muted)] transition-colors"
            >
              Continue shopping
            </Link>
          </div>
          <p className="mt-8 text-xs text-[var(--muted-fg)]">
            Didn&apos;t get an email within 10 minutes? Reply to any of our emails and we&apos;ll sort it out.
          </p>
        </>
      ) : (
        <>
          <Loader2 className="w-16 h-16 text-[var(--terracotta)] mx-auto mb-6 animate-spin" />
          <h1 className="font-serif text-3xl text-[var(--ink-forest)]">Confirming your payment…</h1>
          <p className="mt-3 text-[var(--muted-fg)] text-sm">This will only take a moment.</p>
        </>
      )}
    </div>
  );
}
